import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import carBikeImage from "../../assets/car-bike.png";
import styles from "./DLVerification.module.css";

function DLVerification() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRefs = useRef({
    dlFront: null,
    dlBack: null,
    aadharFront: null,
    aadharBack: null,
  });

  const [formData, setFormData] = useState({
    name: "",
    aadharMobile: "",
  });
  const [files, setFiles] = useState({
    dlFront: null,
    dlBack: null,
    aadharFront: null,
    aadharBack: null,
  });
  const [previews, setPreviews] = useState({
    dlFront: null,
    dlBack: null,
    aadharFront: null,
    aadharBack: null,
  });
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [currentCaptureType, setCurrentCaptureType] = useState(null);
  const [cameraSupported, setCameraSupported] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Check camera support on mount
  useEffect(() => {
    checkCameraSupport();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const checkCameraSupport = () => {
    const isSecureContext = 
      window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost";
    const hasGetUserMedia =
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ||
      !!(navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia);
    setCameraSupported(isSecureContext && hasGetUserMedia);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      if (file && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      } else {
        resolve(null);
      }
    });
  };

  const handleFileChange = async (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Max size is 10MB.`);
        return;
      }
      // Validate file type
      const allowedTypes = [
        "image/jpeg","image/jpg","image/png","image/gif","image/bmp","image/webp","image/heic","image/heif"
      ];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setError(`File type ${file.type} not supported. Use JPG, PNG, GIF, BMP, WebP, HEIC, or HEIF.`);
        return;
      }
      setFiles((prev) => ({ ...prev, [fileType]: file }));
      const preview = await createFilePreview(file);
      setPreviews((prev) => ({ ...prev, [fileType]: preview }));
      setError(null);
    }
  };

  const getUserMediaWithFallback = async (constraints) => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      return await navigator.mediaDevices.getUserMedia(constraints);
    }
    const getUserMedia = navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia;
    if (getUserMedia) {
      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    }
    throw new Error("getUserMedia is not supported in this browser");
  };

  const startCamera = async (fileType) => {
    try {
      setError(null);
      setCurrentCaptureType(fileType);

      if (!cameraSupported) {
        setError("Camera is not supported in this browser or context. Use file upload.");
        return;
      }
      setShowCamera(true);

      const constraints = [
        { video: { facingMode: { exact: "environment" }, width: { ideal: 1920, min: 640 }, height: { ideal: 1080, min: 480 }, aspectRatio: 16/9 }},
        { video: { facingMode: "environment", width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 }}},
        { video: { facingMode: "user", width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 }}},
        { video: { width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 }}},
        { video: true }
      ];
      let stream = null;
      let lastError = null;
      for (const constraint of constraints) {
        try {
          stream = await getUserMediaWithFallback(constraint);
          if (stream) break;
        } catch (err) {
          lastError = err;
        }
      }
      if (!stream) throw lastError || new Error("No camera stream available");

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((err) => {
              setError("Failed to start video. Please allow camera permissions.");
            });
          }
        };
        videoRef.current.oncanplay = () => {
          const loading = document.querySelector(`.${styles.cameraLoading}`);
          if (loading) loading.style.display = "none";
        };
        videoRef.current.onerror = (videoError) => {
          setError("Video playback error. Try again.");
        };
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.setAttribute("webkit-playsinline", "true");
        videoRef.current.muted = true;
        videoRef.current.autoplay = true;
      }
    } catch (err) {
      setShowCamera(false);
      let errorMessage = "Camera access failed. ";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMessage += "Allow camera permission and try again.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorMessage += "No camera found on this device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        errorMessage += "Camera is being used by another app.";
      } else if (err.name === "OverconstrainedError" || err.name === "ConstraintNotSatisfiedError") {
        errorMessage += "Camera constraints not supported.";
      } else if (err.name === "NotSupportedError") {
        errorMessage += "Camera not supported in this browser.";
      } else if (err.name === "SecurityError") {
        errorMessage += "Camera access blocked due to security.";
      } else {
        errorMessage += `${err.message}`;
      }
      errorMessage += " Use file upload instead.";
      setError(errorMessage);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setError("Camera not ready. Please try again.");
      return;
    }
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError("Video not ready. Wait for camera to load.");
      return;
    }
    try {
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        if (blob && blob.size > 0) {
          const timestamp = new Date().getTime();
          const file = new File([blob], `${currentCaptureType}_captured_${timestamp}.jpg`, {
            type: "image/jpeg",
            lastModified: timestamp,
          });
          setFiles((prev) => ({ ...prev, [currentCaptureType]: file }));
          const preview = await createFilePreview(file);
          setPreviews((prev) => ({ ...prev, [currentCaptureType]: preview }));
          closeCamera();
          setError(null);
        } else {
          setError("Failed to capture photo. Try again.");
        }
      }, "image/jpeg", 0.95);
    } catch (err) {
      setError("Failed to capture photo. Try again.");
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
      videoRef.current.oncanplay = null;
      videoRef.current.onerror = null;
    }
    setShowCamera(false);
    setCurrentCaptureType(null);
  };

  const removeFile = (fileType) => {
    setFiles((prev) => ({ ...prev, [fileType]: null }));
    setPreviews((prev) => ({ ...prev, [fileType]: null }));
    if (fileInputRefs.current[fileType]) {
      fileInputRefs.current[fileType].value = "";
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const token = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjI5ZDYwNjRiLTFkZGItNDg2ZC1hMGQ3LTE0ZGQ2MTRhZjA2OCIsImlhdCI6MTc1NTUxMjE2OSwiZXhwIjoxNzU4MTA0MTY5fQ.QULoq5XM4S5a2S_DDG6Ed2H-KjjlnQ9AGdVQ0q_oC-E";
    const vehicle_id = "138f9d80-14fc-47c6-bce9-a21a4b4b3f28";
    if (!formData.name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!formData.aadharMobile.trim()) {
      setError("Please enter your Aadhar registered mobile number.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.aadharMobile)) {
      setError("Enter valid 10-digit Aadhar mobile number.");
      return;
    }
    const requiredFiles = ["dlFront", "dlBack", "aadharFront", "aadharBack"];
    const missingFiles = requiredFiles.filter((fileType) => !files[fileType]);
    if (missingFiles.length > 0) {
      setError(
        `Upload all required docs: ${missingFiles
          .map((f) =>
            f.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
          )
          .join(", ")}`
      );
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("service_type", "DLVerification");
      formDataToSend.append("vehicle_id", vehicle_id);
      formDataToSend.append(
        "additional_info",
        JSON.stringify({
          aadhar_mobile: formData.aadharMobile,
          name: formData.name,
        })
      );
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          formDataToSend.append("documents", file);
        }
      });

      const xhr = new XMLHttpRequest();
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            setUploadProgress((prev) => ({
              ...prev,
              overall: Math.round(percentComplete),
            }));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };
        xhr.onerror = () => reject(new Error("Network error occurred"));
        xhr.ontimeout = () => reject(new Error("Upload timeout"));
        xhr.open("POST", "https://api.rtoxpert.com/api/service-requests");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 120000;
        xhr.send(formDataToSend);
      });
      await uploadPromise;
      setShowPopup(true);
      setUploadProgress({});
      setTimeout(() => {
        setShowPopup(false);
        navigate("/profile");
      }, 3000);
    } catch (err) {
      let errorMessage = "Submission failed: ";
      if (err.message.includes("Network error")) {
        errorMessage += "Check your internet connection and try again.";
      } else if (err.message.includes("timeout")) {
        errorMessage += "Upload timeout. Try again with a better connection.";
      } else if (err.message.includes("413")) {
        errorMessage += "Files too large. Reduce file sizes and try again.";
      } else if (err.message.includes("400")) {
        errorMessage += "Invalid file format or data. Check your uploads.";
      } else if (err.message.includes("401")) {
        errorMessage += "Authentication failed. Please login again.";
      } else {
        errorMessage += err.message || "Unknown error occurred.";
      }
      setError(errorMessage);
      setUploadProgress({});
    } finally {
      setIsSubmitting(false);
    }
  };

  // File upload trigger
  const triggerFileUpload = (fileType) => {
    if (fileInputRefs.current[fileType]) {
      fileInputRefs.current[fileType].click();
    }
  };

  // Camera Modal
  const CameraModal = () => (
    <div className={styles.cameraModal} onClick={(e) => e.target === e.currentTarget && closeCamera()}>
      <div className={styles.cameraContainer}>
        <div className={styles.cameraHeader}>
          <h3>
            Capture{" "}
            {currentCaptureType
              ?.replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())}
          </h3>
          <button className={styles.closeButton} onClick={closeCamera} aria-label="Close camera">
            ✕
          </button>
        </div>
        <div className={styles.videoContainer}>
          <div className={styles.cameraLoading}>
            <div className={styles.loadingSpinner}></div>
            <p>Starting camera...</p>
          </div>
          <video
            ref={videoRef}
            className={styles.video}
            autoPlay
            playsInline
            muted
            webkit-playsinline="true"
            onLoadedData={() => {
              const loading = document.querySelector(`.${styles.cameraLoading}`);
              if (loading) loading.style.display = "none";
            }}
            onError={(e) => {
              setError("Video playback failed. Try again or use file upload.");
            }}
          />
          <canvas
            ref={canvasRef}
            className={styles.canvas}
            style={{ display: "none" }}
          />
          <div className={styles.cameraOverlay}>
            <div className={styles.documentFrame}>
              <div className={styles.frameCorner} style={{ top: 0, left: 0 }}></div>
              <div className={styles.frameCorner} style={{ top: 0, right: 0 }}></div>
              <div className={styles.frameCorner} style={{ bottom: 0, left: 0 }}></div>
              <div className={styles.frameCorner} style={{ bottom: 0, right: 0 }}></div>
            </div>
          </div>
        </div>
        <div className={styles.cameraInstructions}>
          <p>📋 Position the document within the frame</p>
          <p>💡 Ensure good lighting and focus</p>
        </div>
        <div className={styles.cameraControls}>
          <button
            className={styles.captureButton}
            onClick={capturePhoto}
            aria-label="Capture photo"
          >
            📷 Capture
          </button>
          <button
            className={styles.cancelButton}
            onClick={closeCamera}
            aria-label="Cancel camera"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // DocumentUpload component
  const DocumentUpload = ({ label, fileType, required = true }) => (
    <div className={styles.documentUploadGroup}>
      <label>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      <div className={styles.uploadOptions}>
        <div className={styles.fileUploadWrapper}>
          <button
            type="button"
            className={styles.fileUploadLabel}
            onClick={() => triggerFileUpload(fileType)}
          >
            📁 Choose File
          </button>
          <input
            ref={(el) => (fileInputRefs.current[fileType] = el)}
            type="file"
            className={styles.fileInput}
            onChange={(e) => handleFileChange(e, fileType)}
            accept="image/*,.heic,.heif"
            style={{ display: "none" }}
          />
        </div>
        {cameraSupported && (
          <button
            type="button"
            className={styles.cameraButton}
            onClick={() => startCamera(fileType)}
            aria-label={`Open camera for ${label}`}
          >
            📷 Camera
          </button>
        )}
      </div>
      {files[fileType] && (
        <div className={styles.fileItem}>
          {previews[fileType] && (
            <img
              src={previews[fileType]}
              alt={`Preview of ${label}`}
              className={styles.filePreview}
            />
          )}
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>📄 {files[fileType].name}</span>
            <span className={styles.fileSize}>
              ({(files[fileType].size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
          <button
            type="button"
            className={styles.removeFileButton}
            onClick={() => removeFile(fileType)}
            aria-label={`Remove ${label}`}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
        {error && (
          <div className={styles.errorMessage} role="alert">
            {error}
            <button
              className={styles.dismissError}
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}
        {!cameraSupported && (
          <div className={styles.warningMessage}>
            📱 Camera not available. Please use file upload instead.
          </div>
        )}
        <div className={styles.inputGroup}>
          <label>
            Name <span className={styles.required}>*</span>
          </label>
          <input
            name="name"
            type="text"
            placeholder="Enter your full name"
            className={styles.inputField}
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className={styles.inputGroup}>
          <label>
            Aadhar Registered Mobile Number <span className={styles.required}>*</span>
          </label>
          <input
            name="aadharMobile"
            type="tel"
            placeholder="10-digit mobile number"
            className={styles.inputField}
            value={formData.aadharMobile}
            onChange={handleChange}
            maxLength="10"
            pattern="[6-9][0-9]{9}"
            required
          />
        </div>
        <DocumentUpload label="Driving License - Front Side" fileType="dlFront" />
        <DocumentUpload label="Driving License - Back Side" fileType="dlBack" />
        <DocumentUpload label="Aadhar Card - Front Side" fileType="aadharFront" />
        <DocumentUpload label="Aadhar Card - Back Side" fileType="aadharBack" />
        {uploadProgress.overall > 0 && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${uploadProgress.overall}%` }}
              ></div>
            </div>
            <span className={styles.progressText}>
              Uploading... {uploadProgress.overall}%
            </span>
          </div>
        )}
        <button
          className={`${styles.submitButton} ${
            isSubmitting ? styles.submitting : ""
          }`}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className={styles.loadingSpinner}></div>
              Submitting...
            </>
          ) : (
            "Submit Request"
          )}
        </button>
        {showPopup && (
          <div className={styles.successMessage} role="alert">
            <div className={styles.successIcon}>✅</div>
            <h3>Request Submitted Successfully!</h3>
            <p>Our agent will reach out to you soon.</p>
          </div>
        )}
      </div>
      <div className={styles.imageSection}>
        <img
          src={carBikeImage}
          alt="Vehicle services illustration"
          className={styles.vehicleImage}
        />
      </div>
      {showCamera && <CameraModal />}
    </div>
  );
}

export default DLVerification;
